import React, { useEffect, useState } from "react";
import { 
  Box, 
  Typography, 
  Button, 
  useTheme, 
  useMediaQuery, 
  IconButton,
  Breadcrumbs,
  Link
} from "@mui/material";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/Person";
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { authService } from '../../services/authService';
import { User } from "../../services/userService";

interface BreadcrumbItem {
  label: string;
  onClick?: () => void;
}

interface PageHeaderProps {
  title: string;
  action?: {
    label: string;
    icon?: React.ReactNode;
    onClick: () => void;
  };
  secondaryAction?: {
    label: string;
    icon?: React.ReactNode;
    onClick: () => void;
  };
  breadcrumb?: {
    items: BreadcrumbItem[];
  };
}

const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  action,
  secondaryAction,
  breadcrumb,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const handleLogout = () => {
    authService.logout();
    window.location.href = '/login';
  };

  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const user = await authService.getCurrentUser();
        setUser(user);
      } catch (error) {
        console.error('Error fetching user:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, []);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: breadcrumb ? 1 : 6,
        }}
      >
        <Typography variant="h5">{title}</Typography>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          {secondaryAction && (
            <Button
              variant="outlined"
              startIcon={secondaryAction.icon}
              onClick={secondaryAction.onClick}
            >
              {secondaryAction.label}
            </Button>
          )}
          {action && (
            <Button
              variant="contained"
              startIcon={action.icon}
              onClick={action.onClick}
            >
              {action.label}
            </Button>
          )}
          {!isMobile && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                p: 1,
                pl: 2,
                borderRadius: 2,
                border: "1px solid #e0e0e0",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 500, lineHeight: 1.2 }}
                  >
                    {user?.name}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <PersonIcon sx={{ color: "custom.red", fontSize: "0.875rem" }} />
                    <Typography
                      variant="caption"
                      sx={{ color: "custom.red", display: "block", lineHeight: 1.2 }}
                    >
                      {user?.roles
                        ?.sort((a, b) => {
                          const order: Record<string, number> = { 'admin': 1, 'worker': 2, 'employee': 3 };
                          return (order[a] || 999) - (order[b] || 999);
                        })
                        ?.map((role: string, index: number) => {
                          let roleName;
                          switch(role) {
                            case 'admin': 
                              roleName = 'ผู้ดูแลระบบ';
                              break;
                            case 'worker':
                              roleName = 'เจ้าหน้าที่';
                              break;
                            case 'employee':
                              roleName = 'พนักงาน';
                              break;
                            default:
                              roleName = role;
                          }
                          return (
                            <React.Fragment key={role}>
                              {roleName}
                              {index < (user.roles?.length || 0) - 1 && <br />}
                            </React.Fragment>
                          );
                        })}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              {isTablet ? (
                <IconButton
                  onClick={handleLogout}
                  sx={{
                    ml: 1,
                    bgcolor: "custom.red",
                    "&:hover": {
                      bgcolor: "custom.dark",
                    },
                  }}
                >
                  <LogoutIcon sx={{ color: "white", fontSize: "1.2rem" }} />
                </IconButton>
              ) : (
                <Button
                  variant="contained"
                  startIcon={<LogoutIcon />}
                  onClick={handleLogout}
                  sx={{
                    ml: 2,
                    px: 2,
                    bgcolor: "custom.red",
                    "&:hover": {
                      bgcolor: "custom.dark",
                    },
                  }}
                >
                  ออกจากระบบ
                </Button>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {breadcrumb && (
        <Breadcrumbs 
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          sx={{ mb: 4 }}
        >
          {breadcrumb.items.map((item, index) => {
            if (index === breadcrumb.items.length - 1) {
              return (
                <Typography key={index} color="text.primary">
                  {item.label}
                </Typography>
              );
            }
            return (
              <Link
                key={index}
                underline="hover"
                color="primary"
                onClick={item.onClick}
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': {
                    textDecoration: 'underline',
                  }
                }}
              >
                {item.label}
              </Link>
            );
          })}
        </Breadcrumbs>
      )}
    </Box>
  );
};

export default PageHeader;
